<template>
    <div>

        <!-- Verificación del teléfono: el usuario ya quedó creado, pero el
             wizard no sigue hasta que confirme el código que le llega por
             WhatsApp. Reemplaza al formulario, no lo acompaña. -->
        <VerificarWhatsapp
            v-if="showVerificacion"
            :telefono="telefonoVerificacion"
            :espera-reenvio="esperaReenvio"
            @verificado="$emit('next-step')"
        />

        <form v-else class="bk-form" novalidate @submit.prevent="handleSubmit">

            <!-- 1 · Tus datos (no aparece si viene de Google: ya los tenemos) -->
            <section v-if="!isGooglePhoneOnly" class="bk-card" :class="{ 'is-done': datosOk }" aria-labelledby="rg-s1">
                <header class="bk-card-head">
                    <span class="bk-step" aria-hidden="true">{{ datosOk ? '✓' : '1' }}</span>
                    <div>
                        <h2 id="rg-s1" class="bk-card-title">{{ $t('auth_nuevo.reg.s1.title') }}</h2>
                        <p class="bk-card-desc">{{ $t('auth_nuevo.reg.s1.desc') }}</p>
                    </div>
                </header>

                <GoogleButton separador="despues" />

                <div class="bk-grid-2">
                    <BkField id="rg-first_name" v-model="formData.first_name" autocomplete="given-name" :maxlength="45"
                        :label="$t('auth_nuevo.f.first_name')" :error="errorDe('first_name')" :ok="okDe('first_name')"
                        @input="limpiarApi('nombre')" @blur="tocar('first_name')" />
                    <BkField id="rg-last_name" v-model="formData.last_name" autocomplete="family-name" :maxlength="45"
                        :label="$t('auth_nuevo.f.last_name')" :error="errorDe('last_name')" :ok="okDe('last_name')"
                        @input="limpiarApi('apellido')" @blur="tocar('last_name')" />
                </div>

                <BkField id="rg-email" v-model.trim="formData.email" type="email" inputmode="email" autocomplete="email" :maxlength="150"
                    :label="$t('auth_nuevo.f.email')" :error="errorDe('email')" :ok="okDe('email')"
                    @input="limpiarApi('correo')" @blur="tocar('email')" />

                <BkField id="rg-password" v-model="formData.password" :type="ver ? 'text' : 'password'" autocomplete="new-password" :maxlength="100"
                    :label="$t('auth_nuevo.f.password')" :hint="$t('auth_nuevo.f.password_hint')" :error="errorDe('password')"
                    @input="limpiarApi('hash')" @blur="tocar('password')">
                    <template #accion>
                        <button type="button" :class="['bk-eye', { 'is-on': ver }]"
                            :aria-label="ver ? $t('auth_nuevo.hide') : $t('auth_nuevo.show')"
                            :title="ver ? $t('auth_nuevo.hide') : $t('auth_nuevo.show')"
                            :aria-pressed="String(ver)" @click="ver = !ver">
                            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                <path d="M2 12s3.8-6.5 10-6.5S22 12 22 12s-3.8 6.5-10 6.5S2 12 2 12z" />
                                <circle cx="12" cy="12" r="3.1" />
                                <path v-if="ver" d="M4 20 L20 4" />
                            </svg>
                        </button>
                    </template>
                </BkField>
            </section>

            <!-- 2 · WhatsApp -->
            <section class="bk-card" :class="{ 'is-done': telefonoOk }" aria-labelledby="rg-s2">
                <header class="bk-card-head">
                    <span class="bk-step" aria-hidden="true">{{ telefonoOk ? '✓' : (isGooglePhoneOnly ? '1' : '2') }}</span>
                    <div>
                        <h2 id="rg-s2" class="bk-card-title">{{ $t('auth_nuevo.reg.s2.title') }}</h2>
                        <p class="bk-card-desc">{{ isGooglePhoneOnly ? $t('auth_nuevo.reg.phone_only') : $t('auth_nuevo.reg.s2.desc') }}</p>
                    </div>
                </header>

                <div v-if="alerta" class="bk-auth-alert" role="alert"><p>{{ alerta }}</p></div>

                <div id="rg-telefono-box" :class="['bk-phone', { 'is-invalid': !!errorDe('telefono') }]">
                    <NfCombo
                        v-model="telPais"
                        class="bk-combo"
                        :options="opcionesPrefijo"
                        :label="$t('auth_nuevo.f.country')"
                        :search-placeholder="$t('auth_nuevo.f.country_search')"
                        :empty-text="$t('auth_nuevo.f.country_empty')"
                        show-meta
                        @change="limpiarApi('telefono'); tocar('telefono')"
                    />
                    <BkField id="rg-telefono" :value="formData.phone" type="tel" inputmode="numeric" autocomplete="tel-national" :maxlength="20"
                        :label="$t('auth_nuevo.f.phone')" :hint="$t('auth_nuevo.f.phone_hint')" :error="errorDe('telefono')" :ok="telefonoOk"
                        @input="v => { formData.phone = (v || '').replace(/\D/g, ''); limpiarApi('telefono') }" @blur="tocar('telefono')" />
                </div>

                <button type="submit" class="bk-btn bk-btn--primary bk-auth-submit" :disabled="isLoading">
                    <span v-if="isLoading" class="bk-spinner" aria-hidden="true"></span>
                    {{ isLoading ? (isGooglePhoneOnly ? $t('auth_nuevo.reg.saving') : $t('auth_nuevo.reg.loading')) : $t('auth_nuevo.reg.submit') }}
                </button>

                <p v-if="!isGooglePhoneOnly" class="bk-auth-foot">
                    {{ $t('auth_nuevo.reg.have_account') }}
                    <router-link :to="{ name: 'login' }">{{ $t('auth_nuevo.reg.sign_in') }}</router-link>
                </p>
            </section>
        </form>

    </div>
</template>

<script>

import { mapActions } from "vuex";
import BkField from "@/components/BankInformation/BkField.vue";
import NfCombo from "@/components/Formulario/NfCombo.vue";
import GoogleButton from "@/pages/Registration/GoogleButton.vue";
import VerificarWhatsapp from "@/pages/Registration/Register/VerificarWhatsapp.vue";
import { PAISES_TELEFONO, nombrePais, separarTelefono } from "@/assets/data/paisesTelefono";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Campo del front → campo de la API (los errores del alta vienen por campo de la API).
const API = { first_name: 'nombre', last_name: 'apellido', email: 'correo', password: 'hash', telefono: 'telefono' };
// Orden en que se enfoca el primer campo con error.
const CAMPOS = ['first_name', 'last_name', 'email', 'password', 'telefono'];

export default {
    name: "Step1",
    components: {
        BkField,
        NfCombo,
        GoogleButton,
        VerificarWhatsapp
    },
    data() {
        return {
            formData: {
                first_name: '',
                last_name: '',
                email: '',
                password: '',
                phone: ''
            },
            // ISO del país del teléfono (PAISES_TELEFONO): el prefijo sale de ahí.
            telPais: '',
            ver: false,
            isLoading: false,
            intento: false,
            tocados: {},
            // Errores que devolvió la API, por campo de la API (correo, telefono…).
            errApi: {},
            // Error sin campo (sin conexión, algo inesperado).
            alerta: '',
            // Se prende cuando el usuario ya quedó creado y falta el código.
            showVerificacion: false,
            telefonoVerificacion: '',
            // Segundos hasta poder pedir otro código (0 si el primero no salió).
            esperaReenvio: 60
        };
    },
    computed: {
        isGooglePhoneOnly() {
            return !!this.$route.query.google_phone_only;
        },
        opcionesPrefijo() {
            const locale = this.$i18n.locale;
            return PAISES_TELEFONO
                .map(p => ({ value: p.code, label: nombrePais(p.code, locale), meta: '+' + p.dial, flag: p.code }))
                .sort((a, b) => a.label.localeCompare(b.label, locale));
        },
        // Como lo guarda la API: prefijo + número, solo dígitos ("5491122334455").
        telefono() {
            const pais = PAISES_TELEFONO.find(p => p.code === this.telPais);
            return pais && this.formData.phone ? `${pais.dial}${this.formData.phone}` : '';
        },
        errores() {
            const f = this.formData;
            const e = {};
            if (!this.isGooglePhoneOnly) {
                if (!f.first_name.trim()) e.first_name = this.$t('validations.required');
                if (!f.last_name.trim()) e.last_name = this.$t('validations.required');
                if (!f.email) e.email = this.$t('validations.required');
                else if (!EMAIL_RE.test(f.email)) e.email = this.$t('auth_nuevo.err.email');
                if (!f.password) e.password = this.$t('validations.required');
                else if (f.password.length < 6) e.password = this.$t('auth_nuevo.err.password_min');
            }
            if (!this.telPais || !f.phone) e.telefono = this.$t('validations.required_tel');
            else if (f.phone.length < 6) e.telefono = this.$t('auth_nuevo.err.phone_min');
            return e;
        },
        datosOk() {
            return ['first_name', 'last_name', 'email', 'password'].every(c => !this.errores[c] && !this.errApi[API[c]]);
        },
        telefonoOk() {
            return !this.errores.telefono && !this.errApi.telefono;
        }
    },
    methods: {

        ...mapActions([
            "register",
            "createSession",
            "updatePhone",
            "validarTelefono"
        ]),

        tocar(campo) {
            this.$set(this.tocados, campo, true);
        },
        // El de la API se muestra siempre; el local, al salir del campo o al continuar.
        errorDe(campo) {
            const api = this.errApi[API[campo]];
            if (api) return api;
            return (this.tocados[campo] || this.intento) && this.errores[campo] ? this.errores[campo] : '';
        },
        okDe(campo) {
            const valor = this.formData[campo];
            return !!(valor && String(valor).trim()) && !this.errorDe(campo) && !this.errores[campo];
        },
        limpiarApi(campoApi) {
            if (this.errApi[campoApi]) this.$delete(this.errApi, campoApi);
            this.alerta = '';
        },
        enfocar(campo) {
            const el = campo === 'telefono' && !this.telPais
                ? document.querySelector('#rg-telefono-box .nf-combo-trigger')
                : document.getElementById(`rg-${campo}`);
            if (!el) return;
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            setTimeout(() => el.focus({ preventScroll: true }), 350);
        },

        async handleSubmit() {
            if (this.isLoading) return;
            this.intento = true;
            this.alerta = '';
            const conError = CAMPOS.find(c => this.errores[c] || this.errApi[API[c]]);
            if (conError) {
                this.$nextTick(() => this.enfocar(conError));
                return;
            }

            // Si venimos de Google y sólo hay que pedir teléfono
            if (this.isGooglePhoneOnly) {
                // Construir el objeto perfil a partir del usuario en localStorage
                let profile = JSON.parse(localStorage.getItem('usuario')) || {};
                profile.telefono = this.telefono;

                this.isLoading = true;
                try {
                    // Primero manda el bot: si el número no existe en WhatsApp
                    // no se guarda nada y el error va en el campo del teléfono.
                    await this.validarTelefono(profile.telefono);
                    const respTelefono = await this.updatePhone(profile.telefono);
                    // Con VERIFICACION_NUMERO=false en la API no hay código:
                    // el registro sigue como antes. Tampoco si ese número ya
                    // estaba verificado (la API no manda código nuevo).
                    if (this.sinVerificacion(respTelefono) || this.yaVerificado(respTelefono)) {
                        this.$emit('next-step');
                        return;
                    }
                    // El siguiente paso queda bloqueado hasta que confirme el
                    // código: la API ya le pidió al bot que lo mande.
                    this.irAVerificacion(profile.telefono, respTelefono);
                } catch (e) {
                    this.manejarError(e);
                } finally {
                    this.isLoading = false;
                }

                return;
            }

            // Flujo normal (registro por email)
            let data = {
                nombre: this.formData.first_name.trim(),
                apellido: this.formData.last_name.trim(),
                correo: this.formData.email,
                hash: this.formData.password,
                telefono: this.telefono
            };

            this.isLoading = true;
            try {
                // El alta arranca recién con el visto bueno del bot: un número
                // que no existe en WhatsApp no llegaría nunca a verificarse, y
                // quedaría una cuenta muerta.
                await this.validarTelefono({ telefono: data.telefono, correo: data.correo });

                const response = await this.register(data);
                await this.createSession(response);

                // Con VERIFICACION_NUMERO=false en la API es el registro de
                // antes: no hay código y se pasa directo al siguiente paso.
                if (this.sinVerificacion(response)) {
                    this.$emit('next-step');
                    return;
                }

                // El usuario ya existe, pero el wizard no sigue hasta que
                // confirme el código de WhatsApp. El alta ya disparó el envío.
                this.irAVerificacion(data.telefono, response);
            } catch (e) {
                this.manejarError(e);
            } finally {
                this.isLoading = false;
            }
        },

        /**
         * Los dos errores posibles del Continuar tienen forma distinta: el de
         * la validación del teléfono viene con `motivo`, y el del alta viene
         * como un mapa de campo → códigos. Se reparten por ahí.
         */
        manejarError(e) {
            if (!e || !e.response) {
                console.error('[Registro] sin respuesta de la API', e);
                this.alerta = this.$t('auth_nuevo.err.network');
                return;
            }
            const data = e.response.data || {};

            if (data.motivo) {
                this.mostrarErrorDeTelefono(data.motivo);
            } else {
                this.handleError(data);
            }
            const conError = CAMPOS.find(c => this.errApi[API[c]]);
            if (conError) this.$nextTick(() => this.enfocar(conError));
        },

        /**
         * Pinta el motivo del rechazo en el campo del teléfono. Que el usuario
         * distinga "ese número ya tiene cuenta" de "ese número no tiene
         * WhatsApp" de "no pudimos preguntar" es la diferencia entre corregir
         * el número y quedarse trabado sin saber qué pasó.
         */
        mostrarErrorDeTelefono(motivo) {
            const claves = {
                ya_existe: 'unique',
                no_existe: 'whatsapp',
                // Se muestra igual que `no_existe` a pedido de producto: el
                // usuario ve "no tiene WhatsApp" aunque el bot no haya podido
                // contestar. El motivo real queda en el log de la API.
                sin_respuesta: 'whatsapp',
                demasiados_intentos: 'throttled'
            };

            // Cualquier motivo nuevo cae en el mensaje neutro.
            const clave = claves[motivo] || 'unavailable';
            this.$set(this.errApi, 'telefono', this.$t('validations.error-phone-' + clave));
        },

        // Errores del alta: { correo: ['validation.unique'], ... } → texto en el campo.
        handleError(errors) {
            let alguno = false;
            const front = Object.fromEntries(Object.entries(API).map(([f, a]) => [a, f === 'first_name' ? 'firstname' : f === 'last_name' ? 'lastname' : f === 'telefono' ? 'phone' : f]));
            for (const item of Object.keys(errors)) {
                if (!front[item]) continue;
                for (const codigo of [].concat(errors[item])) {
                    const clave = 'validations.error-' + front[item] + '-' + codigo;
                    this.$set(this.errApi, item, this.$te(clave) ? this.$t(clave) : this.$t('auth_nuevo.err.generic'));
                    alguno = true;
                }
            }
            if (!alguno) this.alerta = this.$t('auth_nuevo.err.generic');
        },

        /**
         * Cambia el formulario por la pantalla de verificación.
         *
         * Hace falta la sesión creada: los endpoints del código van con token,
         * así nadie puede probar códigos contra el idusuario de otro.
         */
        irAVerificacion(telefono, response = null) {
            this.telefonoVerificacion = telefono;
            // Si el webhook del bot falló (`codigo_wsp_enviado: false`) el código
            // no salió: se puede pedir otro enseguida, sin esperar 60 s.
            const data = response && response.data ? response.data : {};
            this.esperaReenvio = data.codigo_wsp_enviado === false ? 0 : 60;
            this.showVerificacion = true;
        },

        /**
         * ¿El número ya estaba verificado? Pasa si el alta por Google vuelve a
         * guardar el mismo número que ya confirmó: la API no manda otro código.
         */
        yaVerificado(response) {
            const user = response && response.data && response.data.user;
            return !!(user && Number(user.telefono_verificado) === 1);
        },

        /**
         * ¿La API tiene la verificación apagada (VERIFICACION_NUMERO=false)?
         * Si no manda el dato (API vieja), se asume que sí verifica.
         */
        sinVerificacion(response) {
            const data = response && response.data ? response.data : {};
            return data.verificacion_numero === false;
        }
    },
    created() {
        // Recarga en medio del registro: el usuario ya existe y lo único que
        // falta es el código, así que se entra directo a la verificación. Sin
        // esto el formulario volvería a intentar el alta y la API contestaría
        // que el correo ya está tomado.
        try {
            const usuario = JSON.parse(localStorage.getItem('usuario'));
            if (usuario && usuario.telefono && usuario.telefono_verificado !== undefined
                && usuario.telefono_verificado !== null && !usuario.telefono_verificado) {
                this.irAVerificacion(usuario.telefono);
                // Recarga: el código pudo haber salido hace rato. Se deja
                // pedir otro ya (la API igual hace respetar los 60 s).
                this.esperaReenvio = 0;
                return;
            }
        } catch (e) {
            console.warn('No se pudo parsear usuario desde localStorage', e);
        }

        // Si venimos desde Google y solo se pide teléfono, precargar datos conocidos
        if (this.isGooglePhoneOnly) {
            try {
                const usuario = JSON.parse(localStorage.getItem('usuario')) || {};
                if (usuario.nombre) this.formData.first_name = usuario.nombre;
                if (usuario.apellido) this.formData.last_name = usuario.apellido;
                if (usuario.correo) this.formData.email = usuario.correo;

                // Si el usuario ya tiene teléfono, separar prefijo y número
                if (usuario.telefono) {
                    const tel = String(usuario.telefono).replace(/[^\d+]/g, '');
                    const { pais, numero } = separarTelefono(tel[0] === '+' ? tel : '+' + tel);
                    this.telPais = pais;
                    this.formData.phone = (pais ? numero : tel).replace(/\D/g, '');
                }
            } catch (e) {
                console.warn('No se pudo parsear usuario desde localStorage', e);
            }
        }
    }
};
</script>
